/**
 * @file `ReaderChromeContext` — reader-specific bits the shared lab app
 *       bar needs, published upward from the reader to the shell.
 * @module @labcraft/lab/ui/lib/reader-chrome
 *
 * The lab app bar ({@link LabAppBar}) renders in {@link LabShell},
 * *above* the routed `<Outlet/>`. Its middle cluster (course label +
 * chapter switcher) and the mirror toggle are reader-only — but the
 * reader page renders *inside* the outlet, a descendant of the bar.
 * React context flows down, so the bar can't read a value the reader
 * provides as a descendant.
 *
 * The fix: {@link ReaderChromeProvider} wraps *both* the bar and the
 * outlet at the shell level and holds the reader-chrome value in state
 * (`null` by default → minimal bar). The reader page calls
 * {@link useSetReaderChrome} in an effect to publish its bits while
 * mounted, and clears them on unmount. The bar reads the current value
 * via {@link useReaderChrome} and renders the reader layout when
 * non-null, the minimal layout otherwise.
 *
 * Orientation lives in the published value as the single source of
 * truth for the live reader layout: both the app-bar mirror button and
 * the Settings "Default layout" control mutate it through
 * `setOrientation`, and the reader's `<SplitLayout/>` renders from the
 * same value (the reader threads its own `orientation` state in).
 */

import {
  createContext,
  useContext,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react'
import type { SplitOrientation } from '../components/split_layout.tsx'
import type { ReportContext } from '../../shared/routes.ts'

/**
 * Prev/next chapter navigation targets for the reader's chapter
 * switcher. `null` at the ends of the course's flat chapter sequence.
 *
 * @property prevHref - SPA path of the previous chapter, or `null`.
 * @property nextHref - SPA path of the next chapter, or `null`.
 * @property nextLocked - whether the next arrow is gated (the chapter's
 *   quiz isn't cleared yet). Kept in lockstep with the reader footer's
 *   next button so the top bar can't bypass the gate. `false` when there
 *   is no next chapter or the chapter isn't gated.
 */
export type ChapterNav = {
  prevHref: string | null
  nextHref: string | null
  nextLocked: boolean
}

/**
 * The reader-specific chrome bits the app bar consumes. Present only
 * while the chapter reader is mounted; `null` elsewhere.
 *
 * @property courseLabel - Course title shown beside the `lc>` mark.
 * @property chapterLabel - Centered switcher label, e.g. "Ch 3 / 6".
 * @property orientation - The live split orientation. Source of truth
 *   for the Settings "Default layout" control; the reader's
 *   `<SplitLayout/>` renders from this value.
 * @property setOrientation - Set the orientation absolutely (Settings
 *   "Default layout").
 * @property chapterNav - Prev/next chapter hrefs for the switcher.
 * @property vmRunning - Whether the chapter's primary VM is currently in
 *   the `running` lifecycle state (actively burning free-tier budget).
 *   The chrome budget meter ticks its live countdown only while this is
 *   `true`; it freezes while the VM is paused/idle/booting, and the
 *   `null` chrome on non-reader screens freezes it entirely (no VM there).
 * @property collectReportContext - Snapshot the reader's current context
 *   (location, active objective, VM state, content version) for the
 *   app-bar "Report an issue" flow (#1672). The app bar can't read reader
 *   state directly (it renders above the outlet), so the reader publishes
 *   this collector; the bar calls it at submit time when the learner opts
 *   to attach context. Absent outside the reader (nothing to attach).
 */
export type ReaderChrome = {
  courseLabel: string
  chapterLabel: string
  orientation: SplitOrientation
  setOrientation: (next: SplitOrientation) => void
  chapterNav: ChapterNav
  vmRunning: boolean
  collectReportContext?: () => ReportContext
}

/**
 * Internal context shape: the published value plus its setter. The
 * setter is what the reader page calls to push its bits up to the
 * shell-level state; `null` value selects the bar's minimal layout.
 *
 * @property value - The current reader chrome, or `null`.
 * @property setValue - Publish (or clear, with `null`) the reader
 *   chrome from the reader page.
 */
type ReaderChromeContextValue = {
  value: ReaderChrome | null
  setValue: (next: ReaderChrome | null) => void
}

/** The reader-chrome context — `null` setter is a no-op default. */
const ReaderChromeContext = createContext<ReaderChromeContextValue>({
  value: null,
  setValue: () => {},
})

/**
 * Props for {@link ReaderChromeProvider}.
 *
 * @property children - The shell subtree (app bar + outlet) that both
 *   reads and writes the reader chrome.
 */
export type ReaderChromeProviderProps = {
  children: ReactNode
}

/**
 * Hold the reader-chrome value at the shell level so the app bar (a
 * sibling above the outlet) can read what the reader page (inside the
 * outlet) publishes. Wrap the shell's bar + outlet in this.
 *
 * @param props - {@link ReaderChromeProviderProps}.
 * @returns The provider element.
 */
export function ReaderChromeProvider(
  props: ReaderChromeProviderProps,
): JSX.Element {
  const [value, setValue] = useState<ReaderChrome | null>(null)
  const ctx = useMemo<ReaderChromeContextValue>(
    () => ({ value, setValue }),
    [value],
  )
  return (
    <ReaderChromeContext.Provider value={ctx}>
      {props.children}
    </ReaderChromeContext.Provider>
  )
}

/**
 * Read the current reader chrome, or `null` when not in the reader.
 * The app bar uses the `null` discriminator to choose its layout.
 *
 * @returns The {@link ReaderChrome}, or `null`.
 */
export function useReaderChrome(): ReaderChrome | null {
  return useContext(ReaderChromeContext).value
}

/**
 * Get the setter the reader page uses to publish its chrome bits up to
 * the shell. The page calls this inside an effect with its live values
 * while mounted, and clears (`null`) on unmount so the next screen
 * shows the minimal bar.
 *
 * @returns The publish/clear setter for {@link ReaderChrome}.
 */
export function useSetReaderChrome(): (next: ReaderChrome | null) => void {
  return useContext(ReaderChromeContext).setValue
}
